import cn from 'classnames'
import { AnimatePresence, motion } from 'framer-motion'
import React, {
  ChangeEvent,
  forwardRef,
  InputHTMLAttributes,
  memo,
  ReactElement,
  Ref,
  useMemo
} from 'react'
import Icon from 'core/components/icons/Icon'
import { IconType } from 'core/components/icons/types'

export interface InputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'prefix'> {
  /**
   * @description determine validation state of the input and apply appropriate styles.
   */
  invalid?: boolean
  /**
   * @description determine networking state of the input (in case of auto-saving) and display a spinner, error icon, or success checkmark.
   */
  status?: 'success' | 'error' | 'loading' | 'idle'
  /**
   * @description icon to display on the right hand side of the input box. Can be used to customize the icon for different validation styles. Overridden by status icons.
   */
  suffix?: `${IconType}` | ReactElement
  prefix?: `${IconType}` | ReactElement
  variant?: 'default' | 'invisible'
  className?: string
  format?: string
  allowClear?: boolean
}

const Input = memo(
  forwardRef(
    (
      {
        variant = 'default',
        className,
        suffix,
        prefix,
        invalid = false,
        status = 'idle',
        value = '',
        disabled,
        allowClear,
        ...props
      }: InputProps,
      ref: Ref<HTMLSpanElement>
    ) => {
      const statusStyles = useMemo(() => {
        return {
          idle:
            variant === 'default'
              ? 'focus-within:outline-primary-0 hover:outline-primary-0 outline-dark-2'
              : 'focus-within:outline-primary-0 hover:outline-primary-0',
          loading: 'outline-dark-2',
          success: '', // no status style
          error: 'text-danger-0 outline-danger-0 hover:outline-danger-0'
        }
      }, [variant])

      const variantStyles: Record<typeof variant, string | undefined> = {
        default: '',
        invisible: 'outline-transparent hover:!bg-white !bg-transparent'
      }

      const clearIcon = useMemo(() => {
        return (
          <Icon
            name="close"
            width={9}
            height={9}
            className={cn(
              value
                ? 'cursor-pointer bg-dark-3 hover:bg-dark-4'
                : 'bg-transparent',
              'rounded-full p-px'
            )}
            color="text-white"
            onClick={() =>
              props?.onChange?.({
                target: {
                  value: ''
                }
              } as ChangeEvent<HTMLInputElement>)
            }
          />
        )
      }, [props, value])

      const prefixEl = useMemo(() => {
        if (!prefix) return null
        if (prefix && (prefix as string) in IconType) {
          return (
            <Icon
              name={prefix as IconType}
              width={12}
              height={12}
              className={cn([
                'group-focus-within:text-dark mr-2 whitespace-nowrap',
                statusStyles[
                  status !== 'idle' ? status : invalid ? 'error' : 'idle'
                ]
              ])}
            />
          )
        }

        return (
          <span
            className={cn([
              'group-focus-within:text-dark mr-2 whitespace-nowrap',
              statusStyles[
                status !== 'idle' ? status : invalid ? 'error' : 'idle'
              ]
            ])}
          >
            {prefix}
          </span>
        )
      }, [invalid, prefix, status, statusStyles])

      const suffixEl = useMemo(() => {
        const isCoreIcon = (suffix as string) in IconType

        if (status === 'idle' && suffix && isCoreIcon) {
          return (
            <Icon
              name={suffix as IconType}
              size={14}
              className="mb-px ml-2"
              noWrapper
            />
          )
        }
        if (status === 'idle' && suffix && !isCoreIcon) {
          return <span className="ml-2">{suffix}</span>
        }
        if (status === 'loading') {
          return (
            <Icon
              name="loading"
              size={14}
              className="mb-[3px] ml-2"
              noWrapper
            />
          )
        }
        if (status === 'success') {
          return (
            <Icon
              name="check-circle-outline"
              size={14}
              className="mb-[2px] ml-2"
              noWrapper
            />
          )
        }
        if (status === 'error') {
          return (
            <Icon
              name="exclamation-mark"
              size={14}
              className="mb-[2px] ml-2 text-white bg-danger rounded-full"
              noWrapper
            />
          )
        }

        return null
      }, [suffix, status])
      return (
        <motion.span
          ref={ref}
          className={cn([
            'group inline-flex items-baseline relative text-default rounded-md outline outline-1 p-2 overflow-clip ',
            disabled
              ? `bg-dark-8 ${props?.readOnly ? 'text-dark' : 'text-dark-3'} outline-dark-2 cursor-not-allowed pointer-events-none`
              : 'bg-white',
            statusStyles[
              status !== 'idle' ? status : invalid ? 'error' : 'idle'
            ],
            variantStyles[variant],
            className,
            props?.readOnly &&
              props.type !== 'select' &&
              'hover:outline-dark-2',
            props?.readOnly &&
              variant === 'invisible' &&
              'focus-within:!outline-transparent hover:!outline-transparent'
          ])}
        >
          <AnimatePresence initial={false}>{prefixEl}</AnimatePresence>

          <input
            className={cn([
              'outline-none border-0 bg-transparent w-full grow text-ellipsis peer !leading-5',
              props.type === 'select' && 'cursor-pointer'
            ])}
            disabled={disabled || status === 'loading'}
            value={value}
            {...props}
          />
          <span
            className={cn([
              'group-focus:text-dark whitespace-nowrap',
              status !== 'error' && !invalid && 'text-dark-3',
              statusStyles[
                status !== 'idle' ? status : invalid ? 'error' : 'idle'
              ]
            ])}
          >
            <AnimatePresence initial={false} mode="wait">
              {status === 'idle' && allowClear && clearIcon}
              {!allowClear && <>{suffixEl}</>}
            </AnimatePresence>
          </span>
        </motion.span>
      )
    }
  )
)

export default Input
